import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPriorityColor, getPriorityLabel } from '../../utils/priorityHelpers';
import toast from 'react-hot-toast';
import { isProfileCompleteForDonation } from '../../utils/profileValidation';
import { useAuth } from '../../context/AuthContext';
import ProfileCompletionModal from '../../components/common/ProfileCompletionModal';
import { useState } from 'react';
import { isSpotlightProject, isUrgentPriority } from '../../utils/projectHighlight';

interface ProjectCardProps {
  project: {
    id: number;
    title: string;
    description: string;
    imageUrl?: string;
    goalAmount: number;
    raisedAmount: number;
    progressPercentage: number;
    status?: string;
    priority?: string;
    location?: string;
    endDate?: string;
    isFeatured?: boolean;
    projectCode?: string;
  };
}

export default function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const priorityColor = getPriorityColor(project.priority);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [missingProfileFields, setMissingProfileFields] = useState<string[]>([]);
  const isSpotlight = isSpotlightProject(project);
  const isUrgent = isUrgentPriority(project);

  const daysLeft = (() => {
    if (!project.endDate) return null;
    const diff = Math.ceil((new Date(project.endDate).getTime() - Date.now()) / 86400000);
    return diff > 0 ? diff : 0;
  })();


  return (
    <>
      <ProfileCompletionModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        missingFields={missingProfileFields}
      />
      <Link
        to={`/projects/${project.id}`}
        className={`card card-lift group flex flex-col h-full ${
          isSpotlight
            ? 'ring-2 ring-red-500/70 shadow-lg shadow-red-500/10 border-red-200'
            : ''
        }`}
      >
        <div className={`relative overflow-hidden bg-gray-100 shrink-0 ${isSpotlight ? 'h-56 lg:h-60' : 'h-52 lg:h-56'}`}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10" />

          {project.imageUrl ? (
            <img
              src={project.imageUrl}
              alt={project.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1558a8 0%, #1d6fce 45%, #0e9f8e 100%)' }}
            >
              <span className="text-white font-bold font-display select-none" style={{ fontSize: '4rem', lineHeight: 1, opacity: 0.9 }}>
                {(project.title || '?').charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-20">
            {isSpotlight && (
              <span className="bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 animate-pulse">
                Urgent Appeal
              </span>
            )}
            {project.isFeatured && (
              <span className="badge-amber text-[10px]">★ Featured</span>
            )}
            {project.priority && (
              <span className={`${priorityColor} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide`}>
                {getPriorityLabel(project.priority)}
              </span>
            )}
          </div>
          {daysLeft !== null && daysLeft > 0 && (
            <span className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-white/90 text-gray-700 text-[11px] font-semibold px-2 py-0.5">
              <svg className="w-3 h-3 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {daysLeft}d left
            </span>
          )}
        </div>

        <div className="p-6 flex flex-col flex-1">
          {project.location && (
            <div className="flex items-center gap-1.5 mb-3 text-gray-400 text-[11px] font-medium">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.828 0L6.343 16.657a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {project.location}
            </div>
          )}

          <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 font-display leading-snug">
            {project.title}
          </h3>
          <p className="text-[13px] text-gray-500 mb-5 line-clamp-2 leading-relaxed flex-shrink-0">
            {project.description}
          </p>

          <div className="mt-auto space-y-4">
            <div className="flex gap-2.5">
              <button
                type="button"
                title="Share"
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  const url = `${window.location.origin}/projects/${project.id}`;
                  if (navigator.share) {
                    navigator.share({ title: project.title, url }).then(() => toast.success('Sharing!')).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(url);
                    toast.success('Link copied!');
                  }
                }}
                className="w-10 h-10 border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-500 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600 transition-colors flex-shrink-0"
              >
                <svg className="w-[17px] h-[17px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (!isAuthenticated) {
                    toast.error('Please sign in to donate');
                    navigate('/login');
                    return;
                  }
                  if (user) {
                    const check = isProfileCompleteForDonation(user);
                    if (!check.isComplete) { setMissingProfileFields(check.missingFields); setIsProfileModalOpen(true); return; }
                  }
                  navigate(`/projects/${project.id}/donate`);
                }}
                className={`flex-1 h-10 text-[12px] font-semibold btn ${isUrgent ? 'bg-red-600 hover:bg-red-500 text-white border-transparent' : 'btn-primary'}`}
              >
                {isSpotlight ? 'Donate — Urgent' : 'Donate now'}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Link>
    </>
  );
}
